import React, { useEffect, useRef } from "react";

// Timed colour sequence for the export-route dashes. It plays once:
//   0-12 s    the original red dash animation (drawn by the SVG)
//   12-112 s  colour cycle - dashes released from Sangli come out in runs of
//             five: 5 orange, 5 white, 5 green, 5 blue, 5 dark blue, repeat
//             (dashes already on a route at 12 s finish their trip in red);
//             green dashes are drawn green (head half) into blood red fading
//             lighter toward the tail
//   112-113 s golden second - 3 golden dashes, every other dash black
//   113-120 s all dashes black
// after which the map stays on the original red animation for good.
//
// Everything runs on one virtual clock (clockRef.current.vt, owned by the map
// so it survives this canvas remounting) that advances at rateRef.current x
// real time, so a speed boost (the Sangli key) speeds up the red animation and the
// coloured phases alike without breaking the handovers between them. The red
// phase's dash offset is set here each frame (same 5 units/s and "2 1" dashes
// the old CSS animation used), rather than by a CSS animation that couldn't
// change speed smoothly.
//
// Each route gets its own speed (within a few % of the original 5 units/s) so
// it completes a whole number of Sangli -> destination trips across the 108 s
// canvas-drawn phases (colour + gold + black), and its dash spacing is nudged
// so the dashes tile the route exactly - the pattern is back where it started
// at 120 s, so the handover back to the red animation is seamless.
export const ORIGINAL_MS = 12000;
export const COLOUR_END_MS = 112000;
export const GOLD_END_MS = 113000;
export const CYCLE_MS = 120000;

const BASE_SPEED = 5; // units/s - the original animation: 12 units per 2.4 s
const BASE_PERIOD = 3; // dash 2 + gap 1, as strokeDasharray="2 1"
const STROKE = 0.4;
const COLOURS = ["#F97316", "#FFFFFF", "#16A34A", "#3B82F6", "#1E3A8A"];
const GREEN = 2;
const WHITE = 1;
const DARK_BLUE = 4;
const GOLDEN_ROUTES = 3;
const RUN = 5; // dashes per colour run
const RED = "rgba(255,0,15,0.5)"; // the original route colour (text-qcyan at 50%)
const GLOW_EASE = 0.06; // per-frame easing of the route glow (~1 s in / out)


// Quadratic bezier with an arc-length table for even spacing along the curve.
const buildRoute = ({ p0, c, p1 }) => {
  const N = 400;
  const pts = [];
  const lens = [0];
  for (let i = 0; i <= N; i++) {
    const t = i / N;
    const u = 1 - t;
    pts.push({ x: u * u * p0.x + 2 * u * t * c.x + t * t * p1.x, y: u * u * p0.y + 2 * u * t * c.y + t * t * p1.y });
    if (i) lens.push(lens[i - 1] + Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y));
  }
  const length = lens[N];
  const at = (s) => {
    s = Math.max(0, Math.min(length, s));
    let lo = 0;
    let hi = N;
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if (lens[mid] < s) lo = mid;
      else hi = mid;
    }
    const f = (s - lens[lo]) / (lens[hi] - lens[lo] || 1);
    return { x: pts[lo].x + (pts[hi].x - pts[lo].x) * f, y: pts[lo].y + (pts[hi].y - pts[lo].y) * f };
  };
  const colourSeconds = (CYCLE_MS - ORIGINAL_MS) / 1000;
  const trips = Math.max(1, Math.round((BASE_SPEED * colourSeconds) / length));
  const period = length / Math.max(1, Math.round(length / BASE_PERIOD));
  return { length, at, trips, speed: (trips * length) / colourSeconds, period, dash: (period * 2) / 3 };
};

// The canvas sits outside the map's zoom layer and stays screen-sized; each
// frame it copies that layer's current CSS transform (viewLayerRef), mid-ease
// included, so the dashes follow the zoom exactly and stay sharp.
const RouteColorCycle = ({
  routes,
  width,
  height,
  rateRef,
  clockRef,
  activeRouteRef,
  svgRoutesRef,
  viewLayerRef,
  onPhase,
  className = ""
}) => {
  const canvasRef = useRef(null);
  const onPhaseRef = useRef(onPhase);
  onPhaseRef.current = onPhase;

  useEffect(() => {
    const canvas = canvasRef.current;
    const c = canvas.getContext("2d");
    const built = routes.map(buildRoute);
    // Golden dashes ride the longest routes.
    const golden = new Set(
      [...built.keys()].sort((a, b) => built[b].length - built[a].length).slice(0, GOLDEN_ROUTES)
    );
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let glowKey = null;
    let glowS = 0;
    let scale = 1; // canvas px per map unit at zoom 1
    let pxPerCss = 1;
    // Layout size (clientWidth), not the on-screen box: the fullscreen map can
    // be rotated 90 degrees on phones, which would swap the box's width/height.
    const resize = () => {
      const cssW = canvas.clientWidth || 1;
      const cssH = canvas.clientHeight || 1;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.max(1, Math.round(cssW * dpr));
      canvas.height = Math.max(1, Math.round(cssH * dpr));
      scale = canvas.width / width;
      pxPerCss = canvas.width / cssW;
    };
    // Current zoom/pan of the map layer, as a canvas transform.
    const applyView = () => {
      const t = viewLayerRef?.current ? getComputedStyle(viewLayerRef.current).transform : "none";
      const m = t && t !== "none" ? new DOMMatrixReadOnly(t) : { a: 1, d: 1, e: 0, f: 0 };
      c.setTransform(scale * m.a, 0, 0, scale * m.d, m.e * pxPerCss, m.f * pxPerCss);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const line = (a, b, style, w) => {
      c.beginPath();
      c.moveTo(a.x, a.y);
      c.lineTo(b.x, b.y);
      c.strokeStyle = style;
      c.lineWidth = w;
      c.stroke();
    };

    // mode: "colour" | "gold" (3 golden dashes, rest black) | "black"
    const draw = (elapsedColourS, mode) => {
      const gold = mode === "gold";
      const mono = mode !== "colour";
      const dark = document.documentElement.classList.contains("dark");
      c.setTransform(1, 0, 0, 1, 0, 0);
      c.clearRect(0, 0, canvas.width, canvas.height);
      applyView();
      c.lineCap = "butt";
      // Glow on the hovered/tapped route: eases in, and out after release.
      const target = activeRouteRef?.current || null;
      if (target) glowKey = target;
      glowS += ((target ? 1 : 0) - glowS) * GLOW_EASE;
      if (glowS < 0.01 && !target) glowKey = null;
      built.forEach((r, ri) => {
        const glowing = glowKey && routes[ri].key === glowKey;
        c.shadowColor = glowing ? routes[ri].glow || "#FF000F" : "transparent";
        c.shadowBlur = glowing ? 10 * glowS * pxPerCss : 0;
        const offset = r.speed * elapsedColourS;
        // Dash j spans [offset - j*period, +dash] - the same layout as the SVG's
        // "2 1" dasharray at the matching dashoffset, so the 12 s handover and
        // the 120 s return to the red animation line up. j is the dash's
        // identity, so it keeps its colour as it travels.
        const jMin = Math.floor((offset - r.length) / r.period);
        const jMax = Math.floor((offset + r.dash) / r.period);
        let goldJ = null;
        if (gold && golden.has(ri)) goldJ = Math.round((offset + r.dash - r.length / 2) / r.period);
        for (let j = jMin; j <= jMax; j++) {
          const head = offset - j * r.period + r.dash;
          const tail = head - r.dash;
          if (head <= 0 || tail >= r.length) continue;
          const a = r.at(tail);
          const b = r.at(head);
          if (mono) {
            if (gold && j === goldJ) {
              c.save();
              c.shadowColor = "#FFD700";
              c.shadowBlur = 4;
              const g = c.createLinearGradient(b.x, b.y, a.x, a.y);
              g.addColorStop(0, "#FFF3B0");
              g.addColorStop(0.4, "#FFD700");
              g.addColorStop(1, "#B8860B");
              line(a, b, g, STROKE * 1.6);
              c.restore();
            } else {
              if (dark) line(a, b, "rgba(255,255,255,0.55)", STROKE + 0.25);
              line(a, b, "#000000", STROKE);
            }
            continue;
          }
          // Dashes j <= 0 were already out on the route when the colours began:
          // they finish their trip red. New ones (j = 1, 2, ...) come in runs.
          if (j <= 0) {
            line(a, b, RED, STROKE);
            continue;
          }
          const ci = Math.floor((j - 1) / RUN) % COLOURS.length;
          if (ci === GREEN) {
            // Head -> centre solid green; from the centre blood red, lightening to the tail.
            const g = c.createLinearGradient(b.x, b.y, a.x, a.y);
            g.addColorStop(0, "#16A34A");
            g.addColorStop(0.5, "#16A34A");
            g.addColorStop(0.5, "#8A0303");
            g.addColorStop(1, "#F4B4B4");
            line(a, b, g, STROKE);
            continue;
          }
          // Outlines keep white visible on light and dark blue visible on dark.
          if (ci === WHITE && !dark) line(a, b, "rgba(0,0,0,0.45)", STROKE + 0.25);
          if (ci === DARK_BLUE && dark) line(a, b, "rgba(255,255,255,0.45)", STROKE + 0.25);
          line(a, b, COLOURS[ci], STROKE);
        }
      });
      c.shadowBlur = 0;
      c.shadowColor = "transparent";
    };

    if (reduced) {
      // No motion: a still snapshot of the coloured phase. "done" is still
      // reported after one sequence's worth of real time.
      onPhaseRef.current?.({ phase: "colour", done: false });
      const redraw = () => draw(0, "colour");
      redraw();
      const ro2 = new ResizeObserver(redraw);
      ro2.observe(canvas);
      const timer = setTimeout(() => onPhaseRef.current?.({ phase: "colour", done: true }), CYCLE_MS);
      return () => {
        ro.disconnect();
        ro2.disconnect();
        clearTimeout(timer);
      };
    }

    // Red phase: dashoffset of the SVG routes, at seconds s into that phase.
    const setSvgOffset = (s) => {
      const off = -((BASE_SPEED * s) % (BASE_PERIOD * 4));
      svgRoutesRef.current?.querySelectorAll("path").forEach((p) => p.setAttribute("stroke-dashoffset", off.toFixed(3)));
    };

    let raf;
    let prev = null;
    let last = { phase: null, done: null };
    const frame = (now) => {
      // Cap the step so a backgrounded tab doesn't jump the sequence forward.
      const dt = prev === null ? 0 : Math.min(now - prev, 100);
      prev = now;
      const clock = clockRef.current;
      clock.vt += dt * (rateRef.current || 1);
      const vt = clock.vt;
      const done = vt >= CYCLE_MS;
      const phase =
        done || vt < ORIGINAL_MS ? "original" : vt < COLOUR_END_MS ? "colour" : vt < GOLD_END_MS ? "gold" : "black";
      if (phase !== last.phase || done !== last.done) {
        last = { phase, done };
        onPhaseRef.current?.(last);
      }
      if (phase === "original") {
        c.setTransform(1, 0, 0, 1, 0, 0);
        c.clearRect(0, 0, canvas.width, canvas.height);
        setSvgOffset((done ? vt - CYCLE_MS : vt) / 1000);
      } else {
        draw((vt - ORIGINAL_MS) / 1000, phase);
      }
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [routes, width, height, rateRef, clockRef, activeRouteRef, svgRoutesRef, viewLayerRef]);

  return <canvas ref={canvasRef} className={`absolute inset-0 w-full h-full pointer-events-none ${className}`} aria-hidden="true" />;
};

export default RouteColorCycle;
